import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, BarChart3, Eye, Lock, Settings, ShieldCheck, Trophy, User, UserRound, Users } from "lucide-react";
import { TargetLogo } from "../../components/Logo";
import { adminLogin, adminSession } from "../../api/client";

const features = [
  { icon: BarChart3, title: "Real-time Results", text: "View live results and monitor competitions as they happen." },
  { icon: Trophy, title: "Leaderboards", text: "Manage rankings, classes, grades and overall standings." },
  { icon: Users, title: "Shooter Management", text: "Add, edit and manage shooter profiles and memberships." },
  { icon: Settings, title: "System Control", text: "Manage users, roles, displays and system settings." },
];

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Where the guard bounced the user from, so they land back on the page they wanted.
  const returnTo = (location.state as { from?: string } | null)?.from || "/admin";

  // Already signed in — no reason to show the form again.
  if (adminSession.valid()) return <Navigate to={returnTo} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await adminLogin(username, password);
      navigate(returnTo, { replace: true });
    } catch (err: any) {
      // A TypeError here is a network failure, not a rejected password.
      setError(
        err instanceof TypeError
          ? "Cannot reach the API. Check that the backend is running."
          : err.message || "Login failed"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.15),transparent_60%)] px-4 py-10">
      <div className="grid w-full max-w-[1100px] overflow-hidden rounded-2xl shadow-modal lg:grid-cols-[44%_56%]">
        {/* Left panel */}
        <div className="hidden flex-col bg-navy-950 p-10 lg:flex">
          <div className="flex items-center gap-4">
            <TargetLogo size={64} />
            <div>
              <div className="text-2xl font-extrabold uppercase leading-tight text-white">
                Shooting
                <br />
                Results Portal
              </div>
              <div className="mt-1.5 border-t-2 border-accent pt-1.5 text-sm font-medium text-slate-400">Admin Portal</div>
            </div>
          </div>
          <h2 className="mt-10 text-xl font-bold leading-snug text-white">
            Manage Events. Track Results.
            <br />
            Display with Confidence.
          </h2>
          <div className="mt-8 flex-1 space-y-6">
            {features.map((f) => (
              <div key={f.title} className="flex gap-4 border-t border-navy-800 pt-6 first:border-0 first:pt-0">
                <f.icon size={22} className="mt-0.5 shrink-0 text-accent" />
                <div>
                  <div className="text-[15px] font-bold text-white">{f.title}</div>
                  <div className="mt-1 text-[13px] leading-relaxed text-slate-400">{f.text}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-slate-500">© {new Date().getFullYear()} GRS Shoot Results Portal. All rights reserved.</p>
        </div>

        {/* Right panel */}
        <div className="flex flex-col justify-center bg-white px-8 py-14 sm:px-16">
          <div className="mx-auto w-full max-w-[420px]">
            <div className="text-center">
              <UserRound size={44} className="mx-auto text-ink" strokeWidth={1.5} />
              <h1 className="mt-3 text-xl font-extrabold tracking-tight text-ink">Admin Login</h1>
              <p className="mt-1 text-[13px] text-muted">Sign in to access the admin dashboard</p>
            </div>

            <form className="mt-8" onSubmit={submit}>
              {error && (
                <div className="mb-4 flex items-center gap-2.5 rounded-lg bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">
                  <AlertCircle size={16} /> {error}
                </div>
              )}
              <label className="label">Username or Email</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="input h-12 pl-9"
                  placeholder="Enter your username or email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <label className="label mt-5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  className="input h-12 pl-9 pr-10"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Eye size={17} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-slate-400" />
              </div>

              <div className="mt-4 flex items-center justify-end">
                <span className="cursor-pointer text-sm font-semibold text-primary hover:underline">Forgot password?</span>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="mt-6 h-12 w-full rounded-lg bg-primary text-[15px] font-bold text-white shadow-sm transition-colors hover:bg-primary-hover disabled:opacity-60"
              >
                {busy ? "Signing In..." : "Sign In"}
              </button>
            </form>

            <p className="mt-8 flex items-center justify-center gap-2 text-center text-[13px] text-muted">
              <ShieldCheck size={14} /> Secure access. All admin activity is logged and monitored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
